import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Reusable +/- stepper widget used three times on the Border component
 * (Border Mixes, 18mm Rigid Trim, 18mm Formable Trim). Renders a label, a
 * decrement/value/increment row, a unit suffix, and an optional "Total
 * coverage" footer for trim lengths.
 *
 * `testidPrefix` controls the data-testid suffixes:
 *   "{prefix}-decrement", "{prefix}-input", "{prefix}-increment"
 */
export default function BorderStepperInput({
  label,
  labelHint,
  lengthSuffixLabel = '(2.5m lengths)',
  value,
  setValue,
  testidPrefix,
  unit = 'mixes',
  showLengthFooter = false,
  className = 'mb-4',
}) {
  return (
    <div className={className}>
      <Label className="text-sm font-medium text-gray-700 mb-2 block">
        {label}{labelHint ? ` ${labelHint}` : ''}
        {showLengthFooter && (
          <span className="text-xs text-gray-500 ml-1">{lengthSuffixLabel}</span>
        )}
      </Label>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => value > 0 && setValue(value - 1)}
          disabled={value <= 0}
          className="h-12 w-12 border-2 border-[#2F5E96] text-[#2F5E96] rounded-xl hover:bg-[#2F5E96]/10 focus:ring-[#2F5E96] focus:ring-offset-0"
          data-testid={`${testidPrefix}-decrement`}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <Input
          type="number"
          min="0"
          value={value}
          onChange={(e) => setValue(Math.max(0, parseInt(e.target.value, 10) || 0))}
          className="h-12 text-lg text-center border-2 focus:border-[#2F5E96] rounded-xl w-24"
          data-testid={`${testidPrefix}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setValue(value + 1)}
          className="h-12 w-12 border-2 border-[#2F5E96] text-[#2F5E96] rounded-xl hover:bg-[#2F5E96]/10 focus:ring-[#2F5E96] focus:ring-offset-0"
          data-testid={`${testidPrefix}-increment`}
        >
          <Plus className="w-4 h-4" />
        </Button>
        <span className="text-sm text-gray-500">{unit}</span>
      </div>
      {showLengthFooter && value > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          Total coverage: {(value * 2.5).toFixed(1)}m
        </p>
      )}
    </div>
  );
}
